import React, { useState, useEffect } from "react";
import { withRouter, Link } from "react-router-dom";
import { connect } from "react-redux";
import { setRequestError } from "redux/system/system.actions";
import {
    DatePicker,
    Table,
    Button,
    Input,
    Icon,
    Tag,
    Tooltip,
    Divider,
    Card,
} from "antd";
import moment from "moment";
import { CSVLink } from "react-csv";
import Highlighter from "react-highlight-words";
import ActionMenu from "globalComponents/ActionMenu";

import { findUserName } from "helpers/userNameFinder";

import http from "services/httpService";
import { apiUrl } from "config.json";

//axios cancellation
const CancelToken = http.CancelToken;
let CancelRequest = undefined;

const CasesReport = ({
    doctors,
    breakpoint,
    clinicID,
    caseListSettings,
    setRequestError,
    ...props
}) => {
    const [casesReportTableLoading, setCasesReportTableLoading] = useState(
        false
    );
    const [cases, setCases] = useState([]);
    const [tableData, setTableData] = useState([]);

    const [searchText, setSearchText] = useState("");

    let searchInput = React.createRef();

    const { RangePicker } = DatePicker;
    // const ButtonGroup = Button.Group;

    const isMobileBreakpoint = breakpoint.isNormalMobile;

    useEffect(() => {
        return () => {
            if (CancelRequest) {
                CancelRequest({
                    responseCancelled: true,
                });
            }
        };
    }, []);

    const handleCaseDateRangeChange = async (date, dateString) => {
        const [dateStart, dateEnd] = dateString;
        if (dateStart !== "" && dateEnd !== "") {
            try {
                setCasesReportTableLoading(true);
                const response = await http.get(
                    `${apiUrl}/report/cases/${clinicID}/${dateStart}/${dateEnd}`,
                    {
                        cancelToken: new CancelToken(function executor(c) {
                            CancelRequest = c;
                        }),
                    }
                );
                setCases(response.data.cases);
                setTableData(response.data.cases);
                setCasesReportTableLoading(false);
            } catch (error) {
                if (!error.message.responseCancelled) {
                    setRequestError({
                        errorMessage:
                            "Something went wrong while fetching your data :(",
                        errorSubMessage: error.message,
                    });
                }
            }
        } else {
            setCases([]);
        }
    };

    const handleTableChange = (pagination, filters, sorter, extra) => {
        setTableData(extra.currentDataSource);
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
        }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={(node) => {
                        searchInput = node;
                    }}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() => handleSearch(selectedKeys, confirm)}
                    style={{ width: 188, marginBottom: 8, display: "block" }}
                />
                <Button
                    type="primary"
                    onClick={() => handleSearch(selectedKeys, confirm)}
                    icon="search"
                    size="small"
                    style={{ width: 90, marginRight: 8 }}
                >
                    Search
                </Button>
                <Button
                    onClick={() => handleReset(clearFilters)}
                    size="small"
                    style={{ width: 90 }}
                >
                    Reset
                </Button>
            </div>
        ),
        filterIcon: (filtered) => (
            <Icon
                type="search"
                style={{ color: filtered ? "#1890ff" : undefined }}
            />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes(value.toLowerCase()),
        onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.select());
            }
        },
        render: (text) => (
            <Highlighter
                highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                searchWords={[searchText]}
                autoEscape
                textToHighlight={text.toString()}
            />
        ),
    });

    const handleSearch = (selectedKeys, confirm) => {
        confirm();
        setSearchText(selectedKeys[0]);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText("");
    };

    const casesReportColumns = [
        {
            title: "Patient Name",
            dataIndex: "patient_name",
            ...getColumnSearchProps("patient_name"),
            sorter: (a, b) => a.patient_name.localeCompare(b.patient_name),
            sortDirections: ["descend", "ascend"],
        },
        {
            title: "Complaint Date",
            dataIndex: "complaint_datetime",
            sorter: (a, b) =>
                new Date(moment(a.complaint_datetime).format("LLL")) -
                new Date(moment(b.complaint_datetime).format("LLL")),
            render: (text, record) =>
                moment(record.complaint_datetime).format("LLL"),
        },
        {
            title: "Chief Complaint",
            dataIndex: "chief_complaint",
            className: "normalWrap",
            render: (text, record) => <div style={{ width: 350 }}>{text}</div>,
        },

        {
            title: "Doctor",
            dataIndex: "doctor_id",
            render: (doctor_id) => findUserName(doctor_id),
            filters: [
                ...doctors
                    .filter((doctor) => doctor.status === "active")
                    .map((doctor) => ({
                        text: doctor.doctor_name,
                        value: doctor.doctor_id,
                    })),
            ],
            //   filterMultiple: false,
            onFilter: (value, record) => {
                return record.doctor_id === value;
            },
            // sorter: (a, b) => a.doctor_name.localeCompare(b.doctor_name),
            // sortDirections: ["descend", "ascend"]
        },
        {
            title: "Case Name",
            dataIndex: "case_name",
            render: (text, record) =>
                text === "" ? <span>&mdash;</span> : text,
        },
        {
            title: "Created By",
            dataIndex: "user_id",
            render: (user_id) => findUserName(user_id),
        },
        {
            title: "Date Created",
            dataIndex: "created_at",
            sorter: (a, b) =>
                new Date(moment(a.created_at).format("LLL")) -
                new Date(moment(b.created_at).format("LLL")),
            render: (text, record) => moment(record.created_at).format("LL"),
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (status) => {
                let statusColor = "";
                switch (status) {
                    case "pending":
                        statusColor = caseListSettings.pendingStatusColor;
                        break;
                    case "hold":
                        statusColor = caseListSettings.holdStatusColor;
                        break;
                    case "no-show":
                        statusColor = caseListSettings.noShowStatusColor;
                        break;
                    case "done":
                        statusColor = caseListSettings.doneStatusColor;
                        break;
                    default:
                        statusColor = "#ccc";
                }

                return (
                    <span>
                        <Tag color={statusColor}>{status}</Tag>
                    </span>
                );
            },
            filters: [
                {
                    text: "Pending",
                    value: "pending",
                },
                {
                    text: "Done",
                    value: "done",
                },
                {
                    text: "Hold",
                    value: "hold",
                },
                {
                    text: "No Show",
                    value: "no-show",
                },
            ],
            // filterMultiple: false,
            onFilter: (value, record) => record.status.indexOf(value) === 0,
        },
        {
            title: "",
            key: "action",
            width: 50,
            render: (text, record) => (
                <ActionMenu
                    menu={[
                        {
                            label: "Open Case",
                            icon: "folder-open",
                            onClick: () =>
                                props.history.push(
                                    `/casenote/${record.case_id}`
                                ),
                        },
                    ]}
                    layout="compress"
                />
            ),
        },
    ];

    const CSVHeaders = [
        {
            label: "Patient Name",
            key: "patient_name",
        },
        {
            label: "Complaint Date",
            key: "complaint_datetime",
        },
        {
            label: "Chief Complaint",
            key: "chief_complaint",
        },
        {
            label: "Doctor",
            key: "doctor_name",
        },
        {
            label: "Case Name",
            key: "case_name",
        },
        {
            label: "Created By",
            key: "created_by",
        },
        {
            label: "Date Created",
            key: "created_at",
        },
        {
            label: "Status",
            key: "status",
        },
    ];

    const CSVData = tableData.map((patientCase) => ({
        ...patientCase,
        complaint_datetime: moment(patientCase.complaint_datetime).format("LL"),
        created_at: moment(patientCase.created_at).format("LL"),
    }));

    return (
        <Card>
            <div style={{ marginBottom: 10 }}>
                <Link to="/reports">
                    <Icon type="arrow-left" /> Back to Reports Menu
                </Link>
            </div>

            <Divider />

            <div style={{ marginBottom: 16 }}>
                {isMobileBreakpoint ? (
                    <div
                        style={{
                            marginBottom: 5,
                        }}
                    >
                        Select Date Range:
                    </div>
                ) : (
                    "Select Date Range: "
                )}
                <RangePicker onChange={handleCaseDateRangeChange} />
            </div>

            <div
                style={{
                    marginBottom: 16,
                    minWidth: 320,
                    overflow: "auto",
                    backgroundColor: "#fff",
                }}
            >
                <Table
                    columns={casesReportColumns}
                    dataSource={cases}
                    rowKey={"case_id"}
                    loading={casesReportTableLoading}
                    pagination={{
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} items`,
                        simple: isMobileBreakpoint ? true : false,
                    }}
                    className="reportsTable"
                    onChange={handleTableChange}
                />
            </div>

            <div style={{ textAlign: "right" }}>
                <CSVLink
                    data={CSVData}
                    headers={CSVHeaders}
                    filename={"cases.csv"}
                >
                    <Tooltip title="Export to Excel">
                        <Button
                            style={{
                                backgroundColor: "#4CAF50",
                                color: "#fff",
                                border: "1px solid #4caf50",
                            }}
                        >
                            <Icon type="download" /> Export to CSV
                        </Button>
                    </Tooltip>
                </CSVLink>
            </div>
        </Card>
    );
};

const mapStateToProps = (state) => ({
    doctors: state.UserDetails.doctors,
    clinicID: state.UserDetails.clinicDetails.clinic_id,
    caseListSettings: state.Settings.caseList,
});

const mapDispatchToProps = (dispatch) => ({
    setRequestError: (data) => dispatch(setRequestError(data)),
});

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(CasesReport)
);
